/** @file The GPU side of a renderer: fixed GL state, the shader program and both stores. */
import { compileSpriteProgram } from './compileSpriteProgram';
import type { SpriteProgram } from './compileSpriteProgram';
import { createChunkStore } from './createChunkStore';
import type { ChunkStore } from './createChunkStore';
import { createPageStore } from './createPageStore';
import type { PageStore } from './createPageStore';

/** Everything that lives in the GL context and is rebuilt after a context restore. */
export interface Gpu {
  program: SpriteProgram;
  chunks: ChunkStore;
  pages: PageStore;
}

/**
 * Sets the fixed state (blend on with FUNC_ADD; depth, cull, scissor, stencil and dither off; texture unit 0;
 * raw unpack: no flip, no premultiply, no colour conversion) and creates the program and both stores.
 */
export function initGpu(gl: WebGL2RenderingContext): Gpu {
  for (const cap of [gl.DEPTH_TEST, gl.CULL_FACE, gl.SCISSOR_TEST, gl.STENCIL_TEST, gl.DITHER]) gl.disable(cap);
  gl.enable(gl.BLEND);
  gl.blendEquation(gl.FUNC_ADD);
  gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
  gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false);
  gl.pixelStorei(gl.UNPACK_COLORSPACE_CONVERSION_WEBGL, gl.NONE);
  gl.pixelStorei(gl.UNPACK_ALIGNMENT, 4);
  gl.activeTexture(gl.TEXTURE0);
  return { program: compileSpriteProgram(gl), chunks: createChunkStore(gl), pages: createPageStore(gl) };
}
